import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "src/components/ui/card";
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import { CheckCircle, User, Users, DollarSign, Clock } from "lucide-react";
import axios from "axios";
import { cn } from "../../../../../../lib/utils";
import { BaseUrlApi } from "../../../../../../lib/api";

export function PriceCard({ price, isSelected, onSelect }) {
  const [features, setFeatures] = useState([]);
  const [loadingFeatures, setLoadingFeatures] = useState(true);

  useEffect(() => {
    const getFeatures = async () => {
      try {
        const { data } = await axios.get(
          `${BaseUrlApi}/price/${price.id}/price-features`
        );
        setFeatures(data.data || []);
      } finally {
        setLoadingFeatures(false);
      }
    };
    getFeatures();
  }, [price.id]);

  const formatCurrency = (price, currency) =>
    `${currency?.symbol || currency?.code} ${price}`;

  return (
    <Card
      className={cn(
        "overflow-hidden h-fit",
        isSelected && "border-2 border-primary"
      )}
    >
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <CardTitle>{price.name}</CardTitle>
          <Badge variant={price.status === 1 ? "default" : "secondary"}>
            {price.status === 1 ? "Active" : "Inactive"}
          </Badge>
        </div>
        <CardDescription>
          {price.type === "individual" ? (
            <div className="flex items-center">
              <User className="h-3 w-3 mx-1" />
              Individual
            </div>
          ) : (
            <div className="flex items-center">
              <Users className="h-3 w-3 mx-1" />
              Group {price.group_num || 0}
            </div>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="text-2xl font-bold">
            {formatCurrency(price.price, price.currency)}
          </div>
          {Number(price.discount) > 0 && (
            <Badge variant="outline" className="text-green-600">
              {price.discount}% OFF
            </Badge>
          )}
        </div>
        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex items-center">
            <Clock className="h-4 w-4 mx-2" />
            Duration: {price.duration}
          </div>
          <div className="flex items-center">
            <DollarSign className="h-4 w-4 mx-2" />
            Currency: {price.currency?.code || "USD"}
          </div>
        </div>
        {loadingFeatures ? (
          <div className="text-sm text-gray-500">Loading features...</div>
        ) : features.length > 0 ? (
          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-700">Features:</p>
            <ul className="text-sm space-y-1">
              {features.map((feature) => (
                <li key={feature.id} className="flex items-start gap-2">
                  <CheckCircle className="h-3 w-3 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-600">{feature.description}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="text-sm text-gray-500">No additional features.</div>
        )}
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          variant={isSelected ? "default" : "outline"}
          size="sm"
          onClick={onSelect}
        >
          {isSelected ? "Selected" : "Select"}
        </Button>
      </CardFooter>
    </Card>
  );
}
